import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useDeleteBiddingIntradayOrder } from '~/hooks/bidding/useDeleteBiddingIntradayOrder'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import type { IntradayDeleteConfirm } from '~/pages/IntradayBidding/hooks/useIntradayDeleteConfirm'
import { formatWithdrawSummary } from '~/pages/IntradayBidding/models/withdrawSummary'

type Props = { confirm: IntradayDeleteConfirm; onDeleted?: () => void }

/** 一覧と詳細で共有する下書き削除確認 (AC-BIN-035)。 */
export const IntradayDeleteConfirmDialog = ({
  confirm,
  onDeleted,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { mutateAsync, isPending } = useDeleteBiddingIntradayOrder()
  const { target } = confirm

  const handleConfirm = async (): Promise<void> => {
    if (target?.updatedAt === undefined) return
    try {
      await mutateAsync({ id: target.id, expectedUpdatedAt: target.updatedAt })
      confirm.closeIfStill(target.id)
      showSuccessToast({
        title: t('page:biddingIntraday.deleteConfirm.success'),
      })
      onDeleted?.()
    } catch {
      showErrorToast({ title: t('page:biddingIntraday.deleteConfirm.error') })
    }
  }

  return (
    <ConfirmDialog
      body={
        target === undefined
          ? null
          : formatWithdrawSummary(t, {
              timeCode: target.timeCode,
              areaCode: target.areaCode,
              direction: target.direction,
              orderMethod: target.orderMethod,
              priceYenPerKwh: target.priceYenPerKwh,
              volumeMw: target.volumeMw,
            })
      }
      confirmLabel={t('page:biddingIntraday.deleteConfirm.confirm')}
      destructive
      isLoading={isPending}
      isOpen={target !== undefined}
      onCancel={confirm.close}
      onConfirm={() => void handleConfirm()}
      title={t('page:biddingIntraday.deleteConfirm.title')}
    />
  )
}
